import React, { useEffect, useMemo, useRef, useState } from 'react';

interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
}

// Lightweight searchable dropdown for country selection
export const CountrySelect: React.FC<CountrySelectProps> = ({ value, onChange, options, placeholder }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    return options.filter(o => o.toLowerCase().includes(q)).slice(0, 50);
  }, [options, query]);

  // Close when clicking outside
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (open) setHighlightIndex(-1);
  }, [open, query]);

  const applySelection = (val: string) => {
    onChange(val);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const choice = filtered[highlightIndex] ?? filtered[0];
      if (choice) applySelection(choice);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Show selected value in the input when not typing
  const display = query || value;

  return (
    <div className="relative" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={display}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Search country'}
        className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent"
      />
      {open && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto bg-white border border-stone-200 shadow-sm rounded-md">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-stone-500">No matches</div>
          ) : (
            filtered.map((opt, idx) => (
              <button
                type="button"
                key={opt}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applySelection(opt)}
                onMouseEnter={() => setHighlightIndex(idx)}
                className={`w-full text-left px-3 py-2 text-sm ${
                  (idx === highlightIndex || opt === value)
                    ? 'bg-greenline-900 text-white'
                    : 'hover:bg-stone-50 text-stone-700'
                }`}
              >
                {opt}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

